import { useExpandWidth } from "@redotech/react-animation/transition";
import { useHtmlEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import {
  useExpensiveLocationHover,
  useHover,
} from "@redotech/react-util/hover";
import { filterTruthy } from "@redotech/util/array";
import classNames from "classnames";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useMatch } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { Flex } from "../../flex";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import { CommandPaletteOpenContext } from "./command-palette-open-context";
import { NavShowLeftPaddingContext } from "./nav-left-padding-context";
import { LinkPrimaryTab, RedoPrimaryNav } from "./primary/redo-primary-nav";
import * as navigationCss from "./redo-navigation.module.css";
import {
  RedoSecondaryNav,
  SecondaryNavigationData,
} from "./secondary/redo-secondary-nav";

/**
 * The id of the tab and the secondary navigation data must match.
 */
export interface PageNavigationEntry {
  isCore?: boolean;
  /** Pins the tab to the top of its section, ahead of the alphabetized rest. */
  pinFirst?: boolean;
  /** Pins the tab to the bottom of its section, after the alphabetized rest. */
  pinLast?: boolean;
  primaryTab: LinkPrimaryTab;
  secondaryNavigationData: SecondaryNavigationData | undefined;
  /** Whether this product is actively enabled for the merchant. */
  isActiveProduct?: boolean;
}

export interface RedoSideNavigationProps {
  isHidden: boolean;
  enterSettingsUrl: string;
  settingsRoot: string;
  pageEntries: PageNavigationEntry[];
  /** Omit to hide settings. `hidden` renders it only to register search items. */
  renderSettingsNav?: (options: { hidden: boolean }) => ReactNode;
  teamSwitcher?: ReactNode;
  primaryNavActions?: ReactNode;
  userProfile?: ReactNode;
}

export const CollapseModeContext = createContext<{
  collapseMode: boolean;
  setCollapseMode: (collapseMode: boolean) => void;
}>({ collapseMode: false, setCollapseMode: (collapseMode: boolean) => {} });

export const RedoSideNavigation = memo(function RedoSideNavigation({
  isHidden,
  enterSettingsUrl,
  settingsRoot,
  pageEntries,
  renderSettingsNav,
  teamSwitcher,
  primaryNavActions,
  userProfile,
}: RedoSideNavigationProps) {
  const [navContainer, setNavContainer] = useState<HTMLDivElement | null>(null);

  const [collapseContainer, setCollapseContainer] =
    useState<HTMLSpanElement | null>(null);

  const [closingOnCollapseToggle, setClosingOnCollapseToggle] = useState(false);

  const [hasLeftSinceCollapse, setHasLeftSinceCollapse] = useState(true);

  const [collapseMode, setCollapseMode] = useLocalStorage(
    "redo_side_navigation_collapse_mode",
    false,
  );

  const handleSetCollapseMode = useHandler((collapseMode: boolean) => {
    if (collapseMode) {
      setClosingOnCollapseToggle(true);
      setHasLeftSinceCollapse(false);
    }
    setCollapseMode(collapseMode);
  });

  const navHovered = useExpensiveLocationHover(navContainer);

  const collapseContainerHovered = useHover(collapseContainer);

  const isHovered = navHovered || collapseContainerHovered;

  useEffect(() => {
    if (!isHovered && !hasLeftSinceCollapse) {
      setHasLeftSinceCollapse(true);
    }
  }, [isHovered, hasLeftSinceCollapse]);

  const location = useLocation();

  const isInSettings = useMatch(`${settingsRoot}/*`);

  useLayoutEffect(() => {
    if (isInSettings && collapseMode) {
      setCollapseMode(false);
    }
  }, [isInSettings, collapseMode, setCollapseMode]);

  const { setShowLeftPadding, setHasSecondaryNav } = useContext(
    NavShowLeftPaddingContext,
  );
  useLayoutEffect(() => {
    setShowLeftPadding(!!isInSettings);
  }, [isInSettings, setShowLeftPadding]);

  const [exitedToLeft, setExitedToLeft] = useState(false);

  const expanded =
    !isHidden &&
    !closingOnCollapseToggle &&
    (!collapseMode || (isHovered && hasLeftSinceCollapse) || exitedToLeft);

  const [animateStyles, animating] = useExpandWidth(
    collapseMode ? true : expanded,
    navContainer,
  );

  const [slideVisible, setSlideVisible] = useState(expanded);

  if (collapseMode && expanded && !slideVisible) {
    setSlideVisible(true);
  }

  const showContent = collapseMode
    ? expanded || slideVisible
    : animating || expanded;

  useHtmlEventListener(navContainer, "transitionend", (e) => {
    if (e.propertyName === "transform" && !expanded) {
      setSlideVisible(false);
    }
    if (
      (e.propertyName === "width" || e.propertyName === "transform") &&
      closingOnCollapseToggle
    ) {
      setClosingOnCollapseToggle(false);
    }
  });

  useHtmlEventListener(collapseContainer, "touchstart", () => {
    if (collapseMode) {
      setCollapseMode(false);
    }
  });

  useHtmlEventListener(document.documentElement, "mouseleave", (e) => {
    if (e.clientX < 10) {
      setExitedToLeft(true);
    }
  });

  useHtmlEventListener(document.documentElement, "mouseenter", () => {
    requestAnimationFrame(() => setExitedToLeft(false));
  });

  // Publish the right edge of the side nav as a CSS variable so floating
  // elements (e.g. feature-onboarding popups) can anchor to it across all
  // nav states: drag-resized primary nav, secondary nav open/closed, and
  // collapse mode (where the nav slides in/out via a CSS transform).
  // ResizeObserver doesn't fire during transform animations, so drive
  // updates with rAF while a transition is running.
  useEffect(() => {
    if (!navContainer) {
      return;
    }
    let rafId: number | null = null;
    const update = () => {
      const right = Math.max(0, navContainer.getBoundingClientRect().right);
      document.documentElement.style.setProperty(
        "--redo-side-nav-right",
        `${right}px`,
      );
    };
    const tick = () => {
      update();
      rafId = requestAnimationFrame(tick);
    };
    const stopTick = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
    const onTransitionRun = (e: TransitionEvent) => {
      if (e.propertyName === "transform" && rafId === null) {
        tick();
      }
    };
    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === "transform") {
        stopTick();
        update();
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(navContainer);
    window.addEventListener("resize", update);
    navContainer.addEventListener("transitionrun", onTransitionRun);
    navContainer.addEventListener("transitionend", onTransitionEnd);
    navContainer.addEventListener("transitioncancel", onTransitionEnd);
    return () => {
      stopTick();
      observer.disconnect();
      window.removeEventListener("resize", update);
      navContainer.removeEventListener("transitionrun", onTransitionRun);
      navContainer.removeEventListener("transitionend", onTransitionEnd);
      navContainer.removeEventListener("transitioncancel", onTransitionEnd);
    };
  }, [navContainer]);

  const pagePrimaryTabs = useMemo(
    () => pageEntries.map((entry) => entry.primaryTab),
    [pageEntries],
  );

  const primaryTabSections = useMemo(() => {
    const core: PageNavigationEntry[] = [];
    const activeProducts: PageNavigationEntry[] = [];
    const inactiveProducts: PageNavigationEntry[] = [];

    for (const entry of pageEntries) {
      if (entry.isCore) {
        core.push(entry);
      } else if (entry.isActiveProduct === false) {
        inactiveProducts.push(entry);
      } else {
        activeProducts.push(entry);
      }
    }

    const pinRank = (entry: PageNavigationEntry) =>
      entry.pinFirst ? -1 : entry.pinLast ? 1 : 0;

    const sortSection = (entries: PageNavigationEntry[]): LinkPrimaryTab[] =>
      entries
        .sort(
          (a, b) =>
            pinRank(a) - pinRank(b) ||
            a.primaryTab.friendlyName.localeCompare(b.primaryTab.friendlyName),
        )
        .map((entry) => entry.primaryTab);

    return {
      core: sortSection(core),
      activeProducts: sortSection(activeProducts),
      inactiveProducts: sortSection(inactiveProducts),
    };
  }, [pageEntries]);

  const selectedPrimaryTab = pagePrimaryTabs.find((tab) =>
    location.pathname.includes(tab.pathRoot),
  );

  // Split secondary navs into visible and invisible for global search
  const [visibleSecondaryNav, invisibleSecondaryNavs, allSecondaryNavEntries] =
    useMemo(() => {
      const allSecondaryNavEntries = filterTruthy(
        pageEntries.map((e) => e.secondaryNavigationData),
      );

      if (!selectedPrimaryTab?.id) {
        return [null, allSecondaryNavEntries, allSecondaryNavEntries];
      }
      const visible =
        allSecondaryNavEntries.find((e) => e.id === selectedPrimaryTab.id) ||
        null;
      const invisible = allSecondaryNavEntries.filter(
        (e) => e.id !== selectedPrimaryTab.id,
      );
      return [visible, invisible, allSecondaryNavEntries];
    }, [pageEntries, selectedPrimaryTab?.id]);

  const visiblePathRoot = pageEntries.find(
    ({ secondaryNavigationData }) =>
      secondaryNavigationData?.id === selectedPrimaryTab?.id,
  )?.primaryTab.pathRoot;

  useLayoutEffect(() => {
    const navActuallyVisible = !collapseMode || expanded;
    setHasSecondaryNav(
      (!!visibleSecondaryNav || !!isInSettings) && navActuallyVisible,
    );
  }, [
    visibleSecondaryNav,
    isInSettings,
    setHasSecondaryNav,
    collapseMode,
    expanded,
  ]);

  if (isInSettings) {
    return (
      <Flex data-arbiter="RedoSideNavigation" grow={1} position="relative">
        <span
          className={navigationCss.collapseBar}
          ref={setCollapseContainer}
        />
        <Flex
          className={classNames(
            darkModeCss.darkMode,
            navigationCss.sideNav,
            collapseMode && navigationCss.collapseMode,
            collapseMode && !expanded && navigationCss.collapsed,
            expanded && navigationCss.expanding,
          )}
          dir="row"
          gap="none"
          grow={1}
          ref={setNavContainer}
          style={collapseMode ? undefined : animateStyles}
        >
          {showContent && renderSettingsNav?.({ hidden: false })}
        </Flex>
        {/* Render invisible main nav items for global search when in settings */}
        <InvisibleNavsForSearch
          invisibleSecondaryNavs={allSecondaryNavEntries}
        />
      </Flex>
    );
  }

  return (
    <Flex data-arbiter="RedoSideNavigation" grow={1} position="relative">
      <span className={navigationCss.collapseBar} ref={setCollapseContainer} />
      <Flex
        className={classNames(
          darkModeCss.darkMode,
          navigationCss.sideNav,
          collapseMode && navigationCss.collapseMode,
          collapseMode && !expanded && navigationCss.collapsed,
          expanded && navigationCss.expanding,
        )}
        dir="row"
        gap="none"
        grow={1}
        ref={setNavContainer}
        style={collapseMode ? undefined : animateStyles}
      >
        {showContent && (
          <>
            <RedoPrimaryNav
              actions={primaryNavActions}
              enterSettingsUrl={renderSettingsNav ? enterSettingsUrl : null}
              isCollapseMode={collapseMode}
              primaryTabSections={primaryTabSections}
              selectedTabId={selectedPrimaryTab?.id}
              settingsRoot={settingsRoot}
              teamSwitcher={teamSwitcher}
              toggleCollapseMode={() => handleSetCollapseMode(!collapseMode)}
              userProfile={userProfile}
            />
            {visibleSecondaryNav && visiblePathRoot && (
              <RedoSecondaryNav
                data={visibleSecondaryNav}
                pathRoot={visiblePathRoot}
              />
            )}
            <InvisibleNavsForSearch
              invisibleSecondaryNavs={invisibleSecondaryNavs}
            />
          </>
        )}
      </Flex>
      {/* Render invisible settings items for global search when not in settings */}
      {renderSettingsNav?.({ hidden: true })}
    </Flex>
  );
});

/**
 * Only renders invisible secondary navs when the command palette is open.
 * This allows nav items to register with the tree context for global search
 * while keeping them out of the DOM during normal operation (preventing
 * Playwright from finding hidden elements).
 */
const InvisibleNavsForSearch = memo(function InvisibleNavsForSearch({
  invisibleSecondaryNavs,
}: {
  invisibleSecondaryNavs: SecondaryNavigationData[];
}) {
  const isCommandPaletteOpen = useContext(CommandPaletteOpenContext);

  if (!isCommandPaletteOpen || invisibleSecondaryNavs.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "none" }}>
      {invisibleSecondaryNavs.map(({ NavContent, name }) => (
        <NavContent key={name} pathRoot="" />
      ))}
    </div>
  );
});
